import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import "./MembershipCards.css";
import { useTranslation } from "react-i18next";
import { formatDate as formatDateShared } from "../../../Components/Common/dateFormat";

// Mapping roles to i18n keys (translated with t() at render)
const roleMapping = {
  ROLE_ADMIN: "users.Admin",
  ROLE_SUPER_ADMIN: "users.SuperAdmin",
  ROLE_EMPLOYEE: "users.Employee",
  ROLE_SUPPORT: "users.Support",
};

const formatDate = (dateString) => formatDateShared(dateString);
const formatDate1 = (dateString) => formatDateShared(dateString);

function TableComponent({
  rows = [],
  columns = [],
  onEdit,
  onRowClick,
  onDelete,
  lowTriggerPoint,
  onRowSelect,
  onSelectAll,
  selectedRows,
  selectAll,
  enableRowClick = false,
  showDelete = false,
}) {
  const { mode } = useThemeContext();
  const borderColor =
    mode === "dark" ? DARKCOLORS.GreyScale20 : LIGHTCOLORS.GreyScale20;
  const borderColor2 =
    mode === "dark" ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
  const borderColor1 =
    mode === "dark" ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const textColor =
    mode === "dark"
      ? DARKCOLORS.HeadingLightBackground
      : LIGHTCOLORS.HeadingLightBackground;
  const backgroundColor22 =
    mode === "dark" ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
  const backgroundColor1 =
    mode === "dark" ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
  const scroll1 =
    mode === "light"
      ? LIGHTCOLORS.BodyDarkBackground
      : DARKCOLORS.BodyLightBackground;
  const scroll =
    mode === "light" ? LIGHTCOLORS.GreyScale40 : DARKCOLORS.GreyScale40;
  const editColor =
    mode === "dark" ? DARKCOLORS.Primary300 : LIGHTCOLORS.Primary300;
  const deleteColor =
    mode === "dark" ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;
  const colorDanger1 =
    mode === "dark" ? DARKCOLORS.Danger100 : LIGHTCOLORS.Danger100;
  const colorWarning =
    mode === "dark" ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
  const colorWarning1 =
    mode === "dark" ? DARKCOLORS.Warning100 : LIGHTCOLORS.Warning100;
  const colorSuccess =
    mode === "dark" ? DARKCOLORS.Secondary500 : LIGHTCOLORS.Secondary500;
  const colorSuccess1 =
    mode === "dark" ? DARKCOLORS.Secondary100 : LIGHTCOLORS.Secondary100;
  const { t } = useTranslation();

  // Status value to translated label mapping for table display
  const statusLabels = {
    Active: t("membershipCards.active"),
    Inactive: t("membershipCards.inactive"),
    Expired: t("membershipCards.expired"),
    Open: t("serviceRequest.open"),
    "In Progress": t("serviceRequest.inprogress"),
    Completed: t("serviceRequest.completed"),
  };
  const getStatusDotStyles = (status) => {
    switch (status) {
      case "Active":
      case "Completed":
        return {
          backgroundColor: colorSuccess,
          border: `3px solid ${colorSuccess1}`,
        };
      case "Inactive":
      case "In Progress":
        return {
          backgroundColor: colorWarning,
          border: `3px solid ${colorWarning1}`,
        };
      case "Expired":
      case "Open":
        return {
          backgroundColor: deleteColor,
          border: `3px solid ${colorDanger1}`,
        };
      default:
        return {
          backgroundColor: LIGHTCOLORS.Alert500,
          border: `3px solid ${LIGHTCOLORS.Alert100}`,
        };
    }
  };

  const CustomEditIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M11.0393 1.83002C10.8752 1.6659 10.6805 1.53572 10.4661 1.44692C10.2517 1.35812 10.022 1.31245 9.78993 1.3125C9.5579 1.31255 9.32815 1.35834 9.11382 1.44724C8.8995 1.53614 8.70479 1.66641 8.54084 1.8306L2.99509 7.38394C2.66465 7.71514 2.47901 8.16384 2.47884 8.63169V10.5001C2.47884 10.7416 2.67484 10.9376 2.91634 10.9376H4.79642C5.26484 10.9376 5.71401 10.7509 6.04476 10.4202L11.5864 4.87502C11.917 4.54367 12.1026 4.09473 12.1026 3.62669C12.1026 3.15864 11.917 2.7097 11.5864 2.37835L11.0393 1.83002ZM2.33301 11.8126C2.21698 11.8126 2.1057 11.8587 2.02365 11.9407C1.9416 12.0228 1.89551 12.1341 1.89551 12.2501C1.89551 12.3661 1.9416 12.4774 2.02365 12.5595C2.1057 12.6415 2.21698 12.6876 2.33301 12.6876H11.6663C11.7824 12.6876 11.8937 12.6415 11.9757 12.5595C12.0577 12.4774 12.1038 12.3661 12.1038 12.2501C12.1038 12.1341 12.0577 12.0228 11.9757 11.9407C11.8937 11.8587 11.7824 11.8126 11.6663 11.8126H2.33301Z"
        fill={editColor}
      />
    </svg>
  );

  const CustomDeleteIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="13"
      height="15"
      viewBox="0 0 12 14"
      fill="none"
    >
      <path
        d="M11.5 3.48991C9.5575 3.29741 7.60333 3.19824 5.655 3.19824C4.5 3.19824 3.345 3.25658 2.19 3.37324L1 3.48991M4.20833 2.90047L4.33667 2.1363C4.43 1.58214 4.5 1.16797 5.48583 1.16797H7.01417C8 1.16797 8.07583 1.60547 8.16333 2.14214L8.29167 2.90047M10.2458 5.33276L9.86661 11.2069C9.80244 12.1228 9.74994 12.8344 8.12244 12.8344H4.37744C2.74994 12.8344 2.69744 12.1228 2.63328 11.2069L2.25411 5.33276M5.27588 9.6263H7.21838M4.79167 7.29297H7.70833"
        stroke={deleteColor}
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );

  return (
    <TableContainer
      className="mem-table-sec"
      sx={{
        // Custom scrollbar on the scrolling element; !important beats Sidebar.css globals
        "&::-webkit-scrollbar": {
          width: "10px !important",
          height: "10px !important",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: scroll1,
          borderRadius: "10px",
          border: `1px solid ${scroll}`,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: scroll,
          borderRadius: "10px",
          border: "3px solid transparent",
          backgroundClip: "padding-box",
          "&:hover": {
            backgroundColor: scroll,
          },
        },
      }}
    >
      <Table
        className="mem-table-sec"
        sx={{
          borderCollapse: "collapse", // Ensure proper collapse styling
          marginX: "4px",
          width: "99%",
          "& .MuiTableCell-root": {
            border: "none", // Remove all borders
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              whiteSpace: "nowrap",
              borderTop: `0.5px solid ${borderColor2}`,
              borderBottom: `0.5px solid ${borderColor2}`,
            }}
          >
            <TableCell sx={{ width: "50px", padding: "6px 12px" }}>
              <Checkbox
                checked={selectAll}
                indeterminate={
                  selectedRows?.length > 0 &&
                  selectedRows?.length < rows?.length
                }
                onChange={onSelectAll}
              />
            </TableCell>
            {columns.map((column, index) => (
              <TableCell
                key={index}
                align={column.align || "left"}
                sx={{
                  color: textColor,
                  padding: "6px 10px",
                  //
                  maxWidth: "auto",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  //
                }}
                className=" main-table-cell"
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{
                color: textColor,
                padding: "6px 10px",
                borderBottom: `1px solid ${borderColor}`,
              }}
              className=" add-machine"
            >
              {t("membershipCards.Action")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{}}>
          {rows?.length > 0 ? (
            rows.map((row, rowIndex) => (
              <TableRow
                hover={!!enableRowClick}
                onClick={enableRowClick ? () => onRowClick(row) : undefined}
                key={rowIndex}
                sx={{
                  "&:nth-of-type(even)": {
                    background: backgroundColor1,
                  },
                  whiteSpace: "nowrap",
                  cursor: enableRowClick ? "pointer" : "default",
                }}
              >
                <TableCell
                  sx={{
                    width: "50px",
                    padding: "6px 12px",
                    borderRadius: "10px 0px 0px 10px",
                  }}
                >
                  <Checkbox
                    checked={selectedRows.some(
                      (selectedRow) => selectedRow.id === row.id,
                    )}
                    onChange={() => onRowSelect(row)}
                  />
                </TableCell>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    align={column.align || "left"}
                    sx={{
                      color: textColor,
                      padding: "5px 10px",
                      maxWidth: "165px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                    className="add-machine mem-table-cell"
                    // title={row[column.field]} // Optional: Tooltip on hover
                  >
                    {column.field === "vendingMachineDetails" ? (
                      <Tooltip
                        title={
                          row[column.field]?.length
                            ? row[column.field]
                                .map(
                                  (machine) =>
                                    `${machine.street} - ${machine.location}`,
                                )
                                .join(", ")
                            : "-"
                        }
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "13px",
                              letterSpacing: "0.8px",
                              lineHeight: "1.4",
                            },
                          },
                        }}
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: "100%",
                          }}
                        >
                          {row[column.field]?.length
                            ? row[column.field]
                                .map(
                                  (machine) =>
                                    `${machine.street} - ${machine.location}`,
                                )
                                .join(", ")
                            : "-"}
                        </span>
                      </Tooltip>
                    ) : column.field === "usageCount" ? (
                      row.totalCoupons ? (
                        (() => {
                          const { usageCount, totalCoupons } = row;
                          const usagePercentage = totalCoupons
                            ? Math.round((usageCount / totalCoupons) * 100)
                            : 0;

                          return (
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  display: "inline-flex",
                                }}
                              >
                                <CircularProgress
                                  variant="determinate"
                                  sx={{
                                    color: borderColor, // Gray color for the total circle
                                  }}
                                  size={35}
                                  thickness={6}
                                  value={100} // Full circle
                                />
                                <CircularProgress
                                  variant="determinate"
                                  sx={{
                                    color: borderColor1, // Blue color for progress
                                    position: "absolute", // Overlay on top of the gray circle
                                  }}
                                  size={35}
                                  thickness={6}
                                  value={usagePercentage} // Dynamic progress value
                                />
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    fontSize: "12px",
                                  }}
                                ></div>
                              </div>
                              <Typography
                                variant="body2"
                                style={{ display: "block", marginLeft: "10px" }}
                              >
                                <p style={{ margin: "0px", fontSize: "16px" }}>
                                  {usageCount}/{totalCoupons}
                                </p>
                                <p style={{ margin: "0px" }}>
                                  {usagePercentage.toFixed(2)}%
                                </p>
                              </Typography>
                            </div>
                          );
                        })()
                      ) : (
                        "N/A"
                      )
                    ) : column.field === "startDate" ||
                      column.field === "endDate" ? (
                      formatDate(row[column.field])
                    ) : column.field === "reportedDate" ? (
                      formatDate1(row[column.field])
                    ) : column.field === "discountPercentage" ? (
                      `${row[column.field]} %`
                    ) : column.field === "couponPrice" ? (
                      `€  ${row[column.field]}`
                    ) : column.field === "authorities" ? (
                      row[column.field]?.length > 1 ? (
                        roleMapping[row[column.field][1]] ? (
                          t(roleMapping[row[column.field][1]])
                        ) : (
                          row[column.field][1]
                        ) // Display second authority
                      ) : (
                        row[column.field]
                          ?.map((role) =>
                            roleMapping[role] ? t(roleMapping[role]) : role,
                          )
                          .join(", ") || "N/A"
                      ) // Otherwise, show first authority
                    ) : column.field === "balance" ? (
                      <Box display="flex" alignItems="center" gap="8px">
                        {row.type === "Registered" ? (
                          "-"
                        ) : (
                          <>
                            <Typography variant="body2">
                              € {Number(row[column.field]).toFixed(2)}
                            </Typography>
                            {/* Show "Low" text if balance is less than or equal to the low trigger point */}
                            {row.balance <= lowTriggerPoint && (
                              <Box
                                sx={{
                                  padding: "5px 8px 3px 8px",
                                  borderRadius: "16px",
                                  backgroundColor: deleteColor,
                                  color: "#fff",
                                  fontSize: "8px",
                                }}
                              >
                                {t("dashboard.low")}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    ) : // ) : column.field === "balance" ? (
                    //     <>
                    //         €&nbsp;{row[column.field]} {/* Adds a space between the euro icon and the number */}
                    //     </>
                    column.field === "location" ||
                      column.field === "description" ||
                      column.field === "issueType" ||
                      column.field === "issueCategory" ? (
                      <Tooltip
                        title={row[column.field] || "N/A"}
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "13px",
                              letterSpacing: "0.8px",
                              lineHeight: "1.4",
                            },
                          },
                        }}
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: "100%",
                          }}
                        >
                          {row[column.field] || "N/A"}
                        </span>
                      </Tooltip>
                    ) : column.render ? (
                      // Column-level render wins, so pages can override the default
                      // status dot (e.g. Discounts pill-style StatusBadge)
                      column.render(row[column.field], row)
                    ) : column.field === "status" ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        gap="8px"
                        justifyContent={
                          column.align === "center" ? "center" : "flex-start"
                        }
                      >
                        <Box
                          sx={{
                            width: "12px",
                            height: "12px",
                            borderRadius: "50%",
                            ...getStatusDotStyles(row[column.field]),
                          }}
                        ></Box>
                        <Typography variant="body2">
                          {statusLabels[row[column.field]] || row[column.field]}
                        </Typography>
                      </Box>
                    ) : (
                      <Tooltip
                        title={
                          row[column.field] || row[column.field] === 0
                            ? row[column.field]
                            : "-"
                        }
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "13px",
                              letterSpacing: "0.8px",
                              lineHeight: "1.4",
                            },
                          },
                        }}
                      >
                        <span
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            maxWidth: "100%",
                          }}
                        >
                          {/* "-" for empty/null values (phone, assignedTo, comments…); keep 0 (bowl counts) */}
                          {row[column.field] || row[column.field] === 0
                            ? row[column.field]
                            : "-"}
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    padding: "6px 12px",
                    borderRadius: "0px 10px 10px 0px",
                  }}
                >
                  <Tooltip title="Edit">
                    <IconButton
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation(); // 🔒 Stops row click from firing
                        onEdit?.(row);
                      }}
                      sx={{
                        padding: "6px",
                        marginRight: "9px",
                        backgroundColor:
                          rowIndex % 2 === 0
                            ? backgroundColor1
                            : backgroundColor22, // Match button background with row color
                        borderRadius: "50%",
                      }}
                    >
                      <CustomEditIcon />
                    </IconButton>
                  </Tooltip>
                  {showDelete &&
                    !row.authorities?.includes("ROLE_SUPER_ADMIN") && (
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation(); // prevent row click
                            onDelete?.(row);
                          }}
                          sx={{
                            padding: "6px",
                            backgroundColor:
                              rowIndex % 2 === 0
                                ? backgroundColor1
                                : backgroundColor22,
                            borderRadius: "50%",
                          }}
                        >
                          <CustomDeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length + 2} align="center">
                {t("membershipCards.Nodataavailable")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TableComponent;
